<template>
  <div class="mobile-home-page">
    <van-nav-bar title="我的学习情况" fixed placeholder />

    <van-pull-refresh v-model="refreshing" @refresh="handleRefresh">
      <div class="page-content" v-loading="loading">

        <van-cell-group inset class="section-card top-card">
          <div class="section-title">学习概览</div>

          <div class="metric-grid">
            <div class="metric-item">
              <div class="metric-label">学习覆盖</div>
              <div class="metric-value">
                {{ overview.learnedQuestionCount || 0 }}<span class="metric-total">/{{ overview.totalQuestionCount || 0 }}</span>
              </div>
              <div class="metric-extra">覆盖率 {{ overview.coverageRate || 0 }}%</div>
            </div>

            <div class="metric-item">
              <div class="metric-label">初始掌握率</div>
              <div class="metric-value">{{ overview.initialMasteryRate || 0 }}%</div>
              <div class="metric-extra">基础水平</div>
            </div>

            <div class="metric-item full-width">
              <div class="metric-label">当前错题</div>
              <div class="metric-value danger">{{ overview.currentWrongCount || 0 }}</div>
              <div class="metric-extra">当前仍未纠正</div>
            </div>
          </div>
        </van-cell-group>

        <div class="summary-tip">
          <div>
            还有 <span class="danger-num">{{ unlearnedQuestionCount }}</span> 题没有练习
          </div>
          <div>
            还有 <span class="danger-num">{{ wrongCount }}</span> 道错题没有纠正
          </div>
        </div>

        <div class="learning-guide-text">
          先练习 → 后纠错 → 最后考试
        </div>

        <van-grid :column-num="3" clickable class="quick-grid enlarge-blue-grid">
          <van-grid-item icon="edit" text="开始练习" @click="$router.push('/m/student/practice')" />
          <van-grid-item icon="warning-o" text="错题本" @click="$router.push('/m/student/wrong')" />
          <van-grid-item icon="records" text="模拟考试" @click="$router.push('/m/student/exam')" />
        </van-grid>

      </div>
    </van-pull-refresh>
  </div>
</template>

<script>
import { getStudentHomeOverview } from '@/api/student/home'
import { reportOnlineStatus } from '@/api/student/onlineStatus'
import { Toast } from 'vant'

export default {
  name: 'MobileStudentHome',

  data() {
    return {
      loading: false,
      refreshing: false,
      overview: {}
    }
  },

  computed: {
    unlearnedQuestionCount() {
      const total = this.overview.totalQuestionCount || 0
      const learned = this.overview.learnedQuestionCount || 0
      return Math.max(total - learned, 0)
    },

    wrongCount() {
      return this.overview.currentWrongCount || 0
    }
  },

  created() {
    this.loadData()
    this.reportHomeStatus()
  },

  methods: {
    async reportHomeStatus() {
      try {
        await reportOnlineStatus('IDLE')
      } catch (e) {
        console.log('上报首页状态失败', e)
      }
    },

    async handleRefresh() {
      await this.loadData(false)
      this.refreshing = false
    },

    async loadData(showLoading = true) {
      if (showLoading) {
        this.loading = true
      }

      try {
        const overviewRes = await getStudentHomeOverview()

        if (overviewRes.code === 200) {
          this.overview = overviewRes.data || {}
        } else {
          Toast.fail(overviewRes.message || '获取学习概览失败')
        }

      } catch (e) {
        Toast.fail('加载数据失败')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.mobile-home-page {
  min-height: 100vh;
  background: #f5f7fa;
}

.page-content {
  padding: 12px 0 18px;
  box-sizing: border-box;
}

.section-card {
  margin-top: 12px;
  overflow: hidden;
}

.top-card {
  margin-top: 0;
}

.section-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
  padding: 12px 14px 4px;
}

/* ✨ 改为两列网格布局，适当减少整体高度 */
.metric-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 8px 12px 16px;
}

.metric-item {
  background: #f7f8fa;
  border-radius: 8px;
  padding: 12px 8px;
  text-align: center;
}

.metric-item.full-width {
  grid-column: span 2; /* 底部这一项横跨两列 */
  padding: 14px 8px;
}

.metric-label {
  color: #646566;
  font-size: 13px;
  margin-bottom: 6px;
}

.metric-value {
  color: #1989fa;
  font-size: 24px;
  font-weight: bold;
  line-height: 1.2;
}

.metric-value.danger {
  color: #ee0a24;
}

.metric-total {
  font-size: 14px;
  font-weight: normal;
  color: #909399;
  margin-left: 2px;
}

.metric-extra {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}

.summary-tip {
  margin: 12px;
  padding: 14px 12px;
  background: #fff;
  border-radius: 10px;
  color: #303133;
  font-size: 15px;
  line-height: 28px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.danger-num {
  color: #ee0a24;
  font-size: 22px;
  font-weight: bold;
  padding: 0 2px;
}

.learning-guide-text {
  text-align: center;
  color: #ee0a24;
  font-size: 22px;
  font-weight: 900;
  margin: 22px 12px 12px;
  letter-spacing: 1.5px;
  text-shadow: 1px 1px 2px rgba(238, 10, 36, 0.1);
}

.quick-grid {
  margin: 0 12px 20px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

::v-deep .enlarge-blue-grid .van-grid-item__content {
  padding: 24px 8px;
}

::v-deep .enlarge-blue-grid .van-icon {
  font-size: 38px !important;
  color: #1989fa !important;
}

::v-deep .enlarge-blue-grid .van-grid-item__text {
  font-size: 16px !important;
  font-weight: bold;
  color: #1989fa !important;
  margin-top: 12px;
}
</style>